import './IceBreakerButtonsStack.pcss';
import { sortBy } from 'lodash';
import React, { useMemo } from 'react';
import ButtonModel, {
  GenericButtonModel,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import { IceBreakerButton } from '../IceBreakerButton/IceBreakerButton';
import { IceBreakerModel } from '@mm/state/model/IceBreakerModel';
import { ActivePathType } from '../../../../../common/widgets/common/ActivePathSelection/ActivePathSelection';
import {
  SortableContainer,
  SortableElement,
} from '../../../../../common/lib/react-sortable-hoc';
import { useButtonHandlers } from '../../../../../common/helpers/ice-breaker-helpers/use-button-handlers';

type Props = {
  iceBreaker: IceBreakerModel;
  openedButtonId: number | undefined;
  setOpenedButtonId: (openedButtonId: number | undefined) => void;
  setActivePathType: (activePathType: ActivePathType) => void;
  setActivePageId: (activePageId: number) => void;
};

const SortableButton = SortableElement(IceBreakerButton);

const ButtonsContainer = SortableContainer(
  ({ children, ...props }: { [key: string]: any }) => (
    <div {...props}>{children}</div>
  ),
);

export const IceBreakerButtonsStack = (props: Props) => {
  const {
    iceBreaker,
    openedButtonId,
    setOpenedButtonId,
    setActivePathType,
    setActivePageId,
  } = props;
  const buttonHandlers = useButtonHandlers();
  const buttons = useMemo(
    (): ButtonModel[] => sortBy(iceBreaker.buttons, ['sortOrder']),
    [iceBreaker.buttons],
  ) as GenericButtonModel<PageButtonConfig>[];

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex === newIndex) return;

    const newButtons = [...buttons];

    const { sortOrder: newSortOrder } = newButtons[newIndex];
    const targetButton = { ...newButtons[oldIndex] };

    buttonHandlers.updateButton(iceBreaker, {
      ...targetButton,
      sortOrder: newSortOrder,
    });

    if (oldIndex < newIndex) {
      for (let i = oldIndex + 1; i <= newIndex; i += 1) {
        const button = newButtons[i];

        buttonHandlers.updateButton(iceBreaker, {
          ...button,
          sortOrder: button.sortOrder - 1,
        });
      }
    }

    if (oldIndex > newIndex) {
      for (let i = newIndex; i < oldIndex; i += 1) {
        const button = newButtons[i];

        buttonHandlers.updateButton(iceBreaker, {
          ...button,
          sortOrder: button.sortOrder + 1,
        });
      }
    }
  };

  return (
    <ButtonsContainer
      onSortEnd={onSortEnd}
      lockAxis="y"
      distance={15}
      lockToContainerEdges
      lockOffset={0}
      className="ice-breaker-buttons-stack"
    >
      {buttons.map((button, index: number) => (
        <SortableButton
          index={index}
          button={button}
          deleteable={buttons.length > 1}
          iceBreaker={iceBreaker}
          key={button.id}
          openedButtonId={openedButtonId}
          setOpenedButtonId={setOpenedButtonId}
          setActivePathType={setActivePathType}
          setActivePageId={setActivePageId}
        />
      ))}
    </ButtonsContainer>
  );
};
